/**
 * Auth domain — mutation helpers and query keys.
 */
import { ApiError, NetworkError, apiErrorFromResponse } from '../../lib/api/error.js';
import { tenantHeaderValue } from '../../stores/tenant.js';
import type { Subject } from '../../stores/auth.js';
import { apiRequest, consumeHostMoved } from '../../lib/api/host-pool.js';

/** Append X-Tenant-Id only in override mode (VITE_TENANT_ID set). */
function tenantHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const tid = tenantHeaderValue();
  return tid ? { ...extra, 'x-tenant-id': tid } : extra;
}

export interface LoginResult {
  accessToken: string;
  refreshToken?: string;
  subjectId?: string;
  user?: {
    id?: string;
    username?: string;
    agentId?: string | null;
    tenantId?: string | null;
  };
}

export async function loginMutation(args: {
  identifier: string;
  password: string;
  captchaToken?: string;
  mfaCode?: string;
}): Promise<LoginResult> {
  let res: Response;
  try {
    res = await apiRequest(`/auth/login`, {
      method: 'POST',
      headers: tenantHeaders({ 'content-type': 'application/json' }),
      credentials: 'include',
      body: JSON.stringify({
        identifier: args.identifier,
        password: args.password,
        subjectKind: 'member',
        ...(tenantHeaderValue() ? { tenantId: tenantHeaderValue() } : {}),
        ...(args.captchaToken ? { captchaToken: args.captchaToken } : {}),
        ...(args.mfaCode ? { mfaCode: args.mfaCode } : {}),
      }),
    });
  } catch (cause) {
    throw new NetworkError((cause as Error).message);
  }

  if (!res.ok) throw await apiErrorFromResponse(res, 'Login failed');

  // A fresh session on the current host: a host move before it explains nothing later.
  consumeHostMoved();
  return (await res.json()) as LoginResult;
}

export interface RegisterResult {
  id: string;
  username: string;
  agentId: string | null;
  tenantId: string | null;
  status: string;
}

export async function registerMutation(args: {
  username: string;
  password: string;
  phone: string;
  phoneCountryCode: string;
  firstName?: string;
  lastName?: string;
  bankCode?: string;
  bankAccountNo?: string;
  bankAccountName?: string;
  tenantId?: string;
  referralCode?: string;
  /** Wins over `referralCode` server-side when it names an active partner of this brand. */
  affCode?: string;
  captchaToken?: string;
}): Promise<RegisterResult> {
  let res: Response;
  try {
    const resolvedTenantId = args.tenantId ?? tenantHeaderValue();
    res = await apiRequest(`/register`, {
      method: 'POST',
      headers: tenantHeaders({ 'content-type': 'application/json' }),
      credentials: 'include',
      body: JSON.stringify({
        username: args.username,
        password: args.password,
        phone: args.phone,
        phoneCountryCode: args.phoneCountryCode,
        ...(args.firstName ? { firstName: args.firstName } : {}),
        ...(args.lastName ? { lastName: args.lastName } : {}),
        bankCode: args.bankCode,
        bankAccountNo: args.bankAccountNo,
        bankAccountName: args.bankAccountName,
        ...(resolvedTenantId ? { tenantId: resolvedTenantId } : {}),
        ...(args.referralCode ? { referralCode: args.referralCode } : {}),
        ...(args.affCode ? { affCode: args.affCode } : {}),
        ...(args.captchaToken ? { captchaToken: args.captchaToken } : {}),
      }),
    });
  } catch (cause) {
    throw new NetworkError((cause as Error).message);
  }

  if (!res.ok) throw await apiErrorFromResponse(res, 'Registration failed');

  return (await res.json()) as RegisterResult;
}

export async function resetPasswordWithBankAccountMutation(args: {
  identifier: string;
  accountNumber: string;
  newPassword: string;
  captchaToken?: string;
}): Promise<void> {
  let res: Response;
  try {
    res = await apiRequest(`/auth/password-reset/reset-with-bank-account`, {
      method: 'POST',
      headers: tenantHeaders({ 'content-type': 'application/json' }),
      body: JSON.stringify({
        identifier: args.identifier,
        accountNumber: args.accountNumber,
        newPassword: args.newPassword,
        ...(args.captchaToken ? { captchaToken: args.captchaToken } : {}),
      }),
    });
  } catch (cause) {
    throw new NetworkError((cause as Error).message);
  }

  if (!res.ok) throw await apiErrorFromResponse(res, 'Reset failed');
}

export async function logoutMutation(refreshToken: string | null): Promise<void> {
  try {
    await apiRequest(`/auth/logout`, {
      method: 'POST',
      headers: tenantHeaders({ 'content-type': 'application/json' }),
      credentials: 'include',
      body: JSON.stringify(refreshToken ? { refreshToken } : {}),
    });
  } catch {
    // best-effort — clear local state regardless
  }
}

/**
 * Fetch member/me to build a Subject after login/refresh.
 * Uses a direct fetch so we can pass a brand-new access token before the
 * auth store is updated (the store token is still the old one at this point).
 */
export async function fetchMeMutation(accessToken?: string): Promise<Subject> {
  const headers = tenantHeaders(
    accessToken ? { authorization: `Bearer ${accessToken}` } : {},
  );
  let res: Response;
  try {
    res = await apiRequest(`/members/me`, {
      credentials: 'include',
      headers,
    });
  } catch (cause) {
    throw new NetworkError((cause as Error).message);
  }
  if (!res.ok) throw new ApiError({ status: res.status, message: 'member not found' });
  const data = (await res.json()) as {
    member?: { id?: string; agentId?: string; username?: string };
  };
  const m = data.member;
  if (!m?.id) throw new ApiError({ status: 404, message: 'member not found' });
  return {
    id: m.id,
    kind: 'player',
    agentId: m.agentId ?? '',
    currency: 'THB',
    displayName: m.username,
  };
}

export const AUTH_QUERY_KEYS = {
  me: ['auth', 'me'] as const,
} as const;
